import { ChangeEvent, FC, useEffect, useLayoutEffect, useMemo, useReducer, useState } from 'react';
import { Field, Form, Label } from '../../Registration.styles';
import { Dropdown, Input, Loading, Skeleton } from '../../../../components';
import { RegistrationFormPage } from '../../templates';
import { useToastStore } from '../../../../stores';
import { isFormValid } from '../../../../utils';
import { Option } from '../../../../components/dropdown/Dropdown';
import { newModelFormInitialState, newModelFormReducer } from './newModel.form';
import { getVehicleBrands } from '../../../../services';

const NewModel: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState<boolean>(false);
	const [procurandoMarcas, setProcurandoMarcas] = useState<boolean>(false);
	const [tiposDeVeiculo, setTiposDeVeiculo] = useState<Option[]>([]);
	const [marcasDeVeiculo, setMarcasDeVeiculo] = useState<Option[]>([]);
	const [state, dispatch] = useReducer(newModelFormReducer, newModelFormInitialState);
	const tipoDeVeiculoSelecionado = useMemo(
		() => tiposDeVeiculo.find((veiculo) => veiculo.value === state.idVeiculoTipo),
		[state.idVeiculoTipo, tiposDeVeiculo]
	);
	const marcaDeVeiculoSelecionada = useMemo(
		() => marcasDeVeiculo.find((marca) => marca.value === state.idVeiculoMarca),
		[state.idVeiculoMarca, marcasDeVeiculo]
	);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		dispatch({ type: 'SET_VALUE', field: name, payload: value });
	};

	const handleSelectOption = (field: string, option: Option) => {
		dispatch({ type: 'SET_SELECT_OPTION', field: field, value: option.value });
	};

	const handleSubmit = () => {
		if (!isFormValid(state)) {
			setToast({ message: 'Preencha todos os campos', variant: 'error' });
			return;
		}

	};

	const resetSelectedOptions = (fields: string[]) => {
		fields.forEach((field) => {
			dispatch({
				type: 'SET_SELECT_OPTION',
				field: field,
				value: 0,
			});
		});
	};

	useLayoutEffect(() => {
		const fetchTipos = async () => {
			setLoading(true);

			try {
				setTiposDeVeiculo([
					{ value: 1, label: 'Carro' },
					{ value: 2, label: 'Moto' },
					{ value: 3, label: 'Caminhão/Ônibus' },
				]);
			} catch {
			} finally {
				setTimeout(() => setLoading(false), 500);
			}
		};

		fetchTipos();
	}, []);

	useEffect(() => {
		const fetchMarcas = async () => {
			if (!state.idVeiculoTipo) return;

			setProcurandoMarcas(true);

			try {
				const response = await getVehicleBrands(state.idVeiculoTipo);

				const opcoesDeMarca: Option[] = response.map((marca) => ({
					value: marca.id,
					label: marca.veiculoMarca ?? '',
				}));

				setMarcasDeVeiculo(opcoesDeMarca);

				const opcoesContemMarcaSelecionada =
					opcoesDeMarca.findIndex((marca) => marca.value === state.idVeiculoMarca) !== -1;

				if (!opcoesContemMarcaSelecionada) {
					resetSelectedOptions(['idVeiculoMarca']);
				}
			} catch {
				setToast({ message: 'Erro ao buscar marcas de veículo.', variant: 'error' });
			} finally {
				setTimeout(() => {
					setProcurandoMarcas(false);
				}, 500);
			}
		};

		fetchMarcas();
	}, [setToast, state.idVeiculoMarca, state.idVeiculoTipo]);

	if (loading) return <Loading fullScreen />;

	return (
		<RegistrationFormPage title='Cadastrar Modelo' onSubmit={handleSubmit}>
			<Form>
				<Label>Tipo</Label>
				<Field>
					<Dropdown
						options={tiposDeVeiculo}
						selected={tipoDeVeiculoSelecionado}
						onSelect={(option: Option) => handleSelectOption('idVeiculoTipo', option)}
					/>
				</Field>
				<Label>Marca</Label>
				<Field>
					{procurandoMarcas ? (
						<Skeleton $borderRadius={12} />
					) : (
						<Dropdown
							searchable
							options={marcasDeVeiculo}
							selected={marcaDeVeiculoSelecionada}
							onSelect={(option: Option) => handleSelectOption('idVeiculoMarca', option)}
							disabled={marcasDeVeiculo.length === 0}
						/>
					)}
				</Field>
				<Label>Descrição</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Descrição'
						value={state.descricao}
						onChange={handleInputChange}
						name='descricao'
					/>
				</Field>
				<Label>Código FIPE</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Código FIPE'
						value={state.codigoModeloFipe}
						onChange={handleInputChange}
						name='codigoModeloFipe'
					/>
				</Field>
			</Form>
		</RegistrationFormPage>
	);
};

export default NewModel;
